
import TagFilter from '../TagFilter';
import { ASSET_USAGE_STATUS_LABELS } from '../../lib/assetUsage';

export default function AssetList({
  concepts,
  filteredAssets,
  assetSearchText,
  setAssetSearchText,
  globalSelectedTags,
  setGlobalSelectedTags,
  allAvailableTags,
  selectedAssetStatus,
  setSelectedAssetStatus,
  setSelectedAssetId,
  getAssetTypeLabel,
  isLargeScreen,
  selectedAssetFilter,
  setSelectedAssetFilter,
}) {
  const assetFilterLabels = {
    dormant: '休眠 Asset',
    review: '見直し Asset',
    archive_candidate: 'アーカイブ候補',
  };

  const filters = (
    <div className="space-y-3">
      <TagFilter
        globalSelectedTags={globalSelectedTags}
        setGlobalSelectedTags={setGlobalSelectedTags}
        allAvailableTags={allAvailableTags}
        themeColor="emerald"
      />
    </div>
  );

  const activeFilters = (selectedAssetStatus !== 'all' || globalSelectedTags.length > 0 || (selectedAssetFilter && selectedAssetFilter !== 'all')) && (
    <div className="flex flex-wrap gap-2 mb-4">
      {selectedAssetStatus !== 'all' && (
        <span className="flex items-center gap-1 bg-emerald-950/40 text-emerald-300 border border-emerald-800/50 px-2 py-1 rounded-lg text-[10px] font-bold">
          状態: {{'active':'使用中','pending':'保留','draft':'下書き'}[selectedAssetStatus]}
          <button onClick={() => setSelectedAssetStatus('all')} className="hover:text-white transition-colors" aria-label="状態フィルタを解除">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </span>
      )}
      {selectedAssetFilter && selectedAssetFilter !== 'all' && (
        <span className="flex items-center gap-1 bg-emerald-950/40 text-emerald-300 border border-emerald-800/50 px-2 py-1 rounded-lg text-[10px] font-bold">
          フィルタ: {assetFilterLabels[selectedAssetFilter] || selectedAssetFilter}
          <button onClick={() => setSelectedAssetFilter('all')} className="hover:text-white transition-colors" aria-label="フィルタを解除">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </span>
      )}
      {globalSelectedTags.map(tag => (
        <span key={tag} className="flex items-center gap-1 bg-gray-800 text-gray-300 border border-gray-700 px-2 py-1 rounded-lg text-[10px] font-bold">
          #{tag}
          <button onClick={() => setGlobalSelectedTags(globalSelectedTags.filter(t => t !== tag))} className="hover:text-white transition-colors" aria-label={`${tag}タグフィルタを解除`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </span>
      ))}
    </div>
  );

  const bottomControls = (
    <div className="shrink-0 px-4 p-3 border-t border-gray-800 bg-gray-900 shadow-2xl space-y-3">
      {filters}
      <div className="relative w-full">
        <input
          type="text"
          value={assetSearchText}
          onChange={(e) => setAssetSearchText(e.target.value)}
          placeholder="Assetを検索..."
          className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-xl px-4 py-2 pl-10 outline-none focus:border-blue-500 transition-all placeholder-gray-500 shadow-inner h-[40px]"
        />
        <svg className="absolute left-3.5 top-3 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        {assetSearchText && (
          <button aria-label="検索をクリア" onClick={() => setAssetSearchText('')} className="absolute right-3 top-3 text-gray-500 hover:text-gray-300">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className={`flex-1 overflow-y-auto overscroll-contain ${isLargeScreen ? 'px-4 py-4' : 'p-4 space-y-3'}`}>
        {activeFilters}
        <div className="space-y-2">
          {filteredAssets.length === 0 && (
            <div className="text-sm text-gray-500 p-8 text-center border border-gray-800 rounded-xl bg-gray-900/50">
              {concepts.length === 0 ? '構築されたAssetはまだありません。' : '条件に合うAssetはありません。'}
            </div>
          )}
          {filteredAssets.map(asset => (
            <button key={asset.id} onClick={() => setSelectedAssetId(asset.id)} className="w-full text-left bg-gray-800/40 hover:bg-gray-800 border border-gray-700/50 hover:border-gray-600 rounded-xl p-3 transition-all shadow-sm group">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-bold text-white group-hover:text-emerald-300 transition-colors flex-1 min-w-0 line-clamp-2">{asset.title}</h3>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-[9px] font-bold text-emerald-400 uppercase border border-emerald-900/50 px-1.5 py-0.5 rounded bg-emerald-950/20">{getAssetTypeLabel(asset.type)}</span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-gray-500">{asset.status}</span>
                <span className="text-[9px] font-bold uppercase tracking-widest text-indigo-400">{ASSET_USAGE_STATUS_LABELS[asset.usageStatus] || asset.usageStatus || 'new'}</span>
                <span className="text-[9px] text-gray-600 ml-auto">30d {asset.referencesLast30Days || 0}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {bottomControls}
    </div>
  );
}
